import React, { useState } from 'react';
import {
    View,
    Text,
    TextInput,
    TouchableOpacity,
    StyleSheet,
    ActivityIndicator,
    Alert,
    Platform,
} from 'react-native';
import { useTheme } from '@/contexts/ThemeContext';
import { sanitizeString, validateEmail, validateName } from '@/utils/validation';
import { logger } from '@/utils/logger';
import { Send, CheckCircle, AlertCircle } from 'lucide-react-native';

interface ContactFormProps {
    startCollapsed?: boolean;
}

const ContactForm = (props: ContactFormProps) => {
    const { colors } = useTheme();

    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [message, setMessage] = useState('');
    const [loading, setLoading] = useState(false);
    const [contentError, setContentError] = useState(false);
    const [lastSubmitTime, setLastSubmitTime] = useState(0);
    const [isExpanded, setIsExpanded] = useState(!props.startCollapsed);

    const RATE_LIMIT_MS = 60000; // 1 minuto de espera entre envios

    const [isSuccess, setIsSuccess] = useState(false);

    const validateForm = () => {
        const nameValidation = validateName(name);
        if (!nameValidation.isValid) {
            Alert.alert('Erro', nameValidation.errors[0]);
            return false;
        }

        const emailValidation = validateEmail(email);
        if (!emailValidation.isValid) {
            Alert.alert('Erro', emailValidation.errors[0]);
            return false;
        }

        if (!message.trim() || message.length < 10) {
            setContentError(true);
            Alert.alert('Erro', 'A mensagem deve ter pelo menos 10 caracteres');
            return false;
        }

        return true;
    };

    const handleMessageChange = (text: string) => {
        setMessage(text);
        if (contentError) setContentError(false);
    };

    const handleReset = () => {
        setIsSuccess(false);
        setIsExpanded(true);
    };

    const handleSubmit = async () => {
        // Rate Limiting no Frontend
        const now = Date.now();
        if (now - lastSubmitTime < RATE_LIMIT_MS) {
            Alert.alert(
                'Aguarde',
                `Por favor, aguarde alguns segundos antes de enviar outra mensagem.`
            );
            return;
        }

        if (!validateForm()) return;

        setLoading(true);

        try {
            const sanitizedData = {
                name: sanitizeString(name),
                email: sanitizeString(email),
                message: sanitizeString(message),
            };

            const apiUrl = '/api/contact';

            const response = await fetch(apiUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(sanitizedData),
            });

            const responseText = await response.text();

            let result;
            try {
                result = JSON.parse(responseText);
            } catch (e) {
                throw new Error(`Erro API: ${response.status} ${response.statusText}`);
            }

            if (response.ok) {
                // Alert.alert('Sucesso', 'Sua mensagem foi enviada com sucesso!');
                setName('');
                setEmail('');
                setMessage('');
                setLastSubmitTime(Date.now());
                logger.info('Contato enviado com sucesso');
                setIsSuccess(true);
            } else {
                throw new Error(result.error || result.details || 'Erro ao enviar mensagem');
            }
        } catch (error: any) {
            logger.error('Erro ao enviar contato', { error: error.message });
            Alert.alert('Erro', `Não foi possível enviar sua mensagem: ${error.message}`);
        } finally {
            setLoading(false);
        }
    };

    const styles = StyleSheet.create({
        container: {
            padding: 24,
            borderRadius: 16,
            backgroundColor: colors.surface,
            borderWidth: 1,
            borderColor: colors.border,
            width: '100%',
            maxWidth: 600,
            alignSelf: 'center',
            marginTop: 24,
            elevation: 2,
            ...(Platform.OS === 'web' ? {
                boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.05)',
            } : {
                shadowOffset: { width: 0, height: 2 },
                shadowOpacity: 0.05,
                shadowRadius: 8,
            }),
        },
        ctaContainer: {
            alignItems: 'center',
            padding: 16,
        },
        successContainer: {
            alignItems: 'center',
            padding: 24,
            gap: 16,
        },
        title: {
            fontSize: 24,
            fontFamily: 'Inter-Bold',
            color: colors.text,
            marginBottom: 8,
            textAlign: 'center',
        },
        subtitle: {
            fontSize: 14,
            fontFamily: 'Inter-Regular',
            color: colors.textSecondary,
            marginBottom: 24,
            textAlign: 'center',
        },
        successTitle: {
            fontSize: 20,
            fontFamily: 'Inter-Bold',
            color: colors.text,
            textAlign: 'center',
        },
        successText: {
            fontSize: 16,
            fontFamily: 'Inter-Regular',
            color: colors.textSecondary,
            textAlign: 'center',
            marginBottom: 16,
        },
        inputGroup: {
            marginBottom: 16,
        },
        label: {
            fontSize: 14,
            fontFamily: 'Inter-Medium',
            color: colors.text,
            marginBottom: 8,
        },
        input: {
            backgroundColor: colors.background,
            borderRadius: 8,
            borderWidth: 1,
            borderColor: colors.border,
            paddingHorizontal: 16,
            paddingVertical: 12,
            fontSize: 16,
            fontFamily: 'Inter-Regular',
            color: colors.text,
        },
        textArea: {
            height: 120,
            textAlignVertical: 'top',
        },
        helperText: {
            fontSize: 12,
            fontFamily: 'Inter-Regular',
            color: colors.textSecondary,
            marginTop: 4,
        },
        helperTextError: {
            color: colors.error || '#DC2626',
            fontFamily: 'Inter-SemiBold',
        },
        button: {
            backgroundColor: colors.primary,
            borderRadius: 25,
            paddingVertical: 16,
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'row',
            gap: 8,
            marginTop: 8,
            opacity: loading ? 0.7 : 1,
        },
        buttonText: {
            color: '#FFFFFF',
            fontSize: 16,
            fontFamily: 'Inter-Bold',
        },
        ctaButton: {
            backgroundColor: colors.primary,
            borderRadius: 25,
            paddingVertical: 12,
            paddingHorizontal: 24,
            alignItems: 'center',
            justifyContent: 'center',
            marginTop: 8,
        },
        successButton: {
            backgroundColor: 'transparent',
            borderWidth: 1,
            borderColor: colors.primary,
            borderRadius: 25,
            paddingVertical: 12,
            paddingHorizontal: 24,
            marginTop: 8,
        },
        successButtonText: {
            color: colors.primary,
            fontSize: 14,
            fontFamily: 'Inter-SemiBold',
        }
    });

    if (!isExpanded) {
        return (
            <View style={styles.container}>
                <View style={styles.ctaContainer}>
                    <Text style={styles.title}>Ainda tem dúvidas?</Text>
                    <Text style={styles.subtitle}>
                        Não encontrou o que procurava? Mande uma mensagem pra gente.
                    </Text>
                    <TouchableOpacity
                        style={styles.ctaButton}
                        onPress={() => setIsExpanded(true)}
                        activeOpacity={0.8}
                    >
                        <Text style={styles.buttonText}>Entrar em Contato</Text>
                    </TouchableOpacity>
                </View>
            </View>
        );
    }

    if (isSuccess) {
        return (
            <View style={styles.container}>
                <View style={styles.successContainer}>
                    <CheckCircle size={64} color={colors.success || '#4CAF50'} />
                    <Text style={styles.successTitle}>Mensagem Enviada!</Text>
                    <Text style={styles.successText}>
                        Obrigado por entrar em contato. Responderemos o mais breve possível no seu email.
                    </Text>
                    <TouchableOpacity
                        style={styles.successButton}
                        onPress={handleReset}
                        activeOpacity={0.8}
                    >
                        <Text style={styles.successButtonText}>Enviar outra mensagem</Text>
                    </TouchableOpacity>
                </View>
            </View>
        );
    }

    return (
        <View style={styles.container}>
            <Text style={styles.title}>Fale Conosco</Text>
            <Text style={styles.subtitle}>
                Tem alguma dúvida ou sugestão? Envie uma mensagem para nós.
            </Text>

            <View style={styles.inputGroup}>
                <Text style={styles.label}>Nome</Text>
                <TextInput
                    style={styles.input}
                    value={name}
                    onChangeText={setName}
                    placeholder="Seu nome"
                    placeholderTextColor={colors.textSecondary}
                    editable={!loading}
                />
            </View>

            <View style={styles.inputGroup}>
                <Text style={styles.label}>E-mail</Text>
                <TextInput
                    style={styles.input}
                    value={email}
                    onChangeText={setEmail}
                    placeholder="seu@email.com"
                    placeholderTextColor={colors.textSecondary}
                    keyboardType="email-address"
                    autoCapitalize="none"
                    editable={!loading}
                />
            </View>

            <View style={styles.inputGroup}>
                <Text style={styles.label}>Mensagem</Text>
                <TextInput
                    style={[
                        styles.input,
                        styles.textArea,
                        contentError && { borderColor: colors.error || '#DC2626', borderWidth: 1 }
                    ]}
                    value={message}
                    onChangeText={handleMessageChange}
                    placeholder="Como podemos ajudar?"
                    placeholderTextColor={colors.textSecondary}
                    multiline
                    numberOfLines={4}
                    editable={!loading}
                />
                <Text style={[
                    styles.helperText,
                    contentError && styles.helperTextError
                ]}>Mínimo de 10 caracteres.</Text>
            </View>

            <TouchableOpacity
                style={styles.button}
                onPress={handleSubmit}
                disabled={loading}
                activeOpacity={0.8}
            >
                {loading ? (
                    <ActivityIndicator color="#FFFFFF" />
                ) : (
                    <>
                        <Send size={20} color="#FFFFFF" />
                        <Text style={styles.buttonText}>Enviar Mensagem</Text>
                    </>
                )}
            </TouchableOpacity>
        </View>
    );
};

export default ContactForm;
